/**
 * The detail page's read-only facts: claim timings and seed outputs.
 *
 * @module features/sandboxes/components/v2/detail/SandboxFactsSections
 */
import { Flex, Heading, Text } from '@chatsdet/ui';
import { formatDurationMs } from '../../../../../lib/formatDuration';
import type { SandboxV2Detail } from '../../../hooks/useSandboxV2Detail';

function ms(value: number): string {
  return value < 1000 ? `${value}ms` : formatDurationMs(value);
}

export function SandboxTimingsSection({ sandbox }: { sandbox: SandboxV2Detail }) {
  if (sandbox.readyMs === null) return null;
  const phases = sandbox.phases
    ? [
        ['Snapshot', sandbox.phases.snapshotMs],
        ['Restore', sandbox.phases.restoreMs],
        ['Prepare', sandbox.phases.prepareMs],
      ] as const
    : [];
  return (
    <section className="flex flex-col gap-2" aria-labelledby="sandbox-v2-timings">
      <Heading level={3} id="sandbox-v2-timings">
        Timings
      </Heading>
      <Flex gap="lg" wrap="wrap" align="baseline">
        <div>
          <Text size="xs" color="muted">
            Ready in
          </Text>
          <Text weight="semibold" className="tabular-nums">
            {ms(sandbox.readyMs)}
          </Text>
        </div>
        {phases.map(([label, value]) => (
          <div key={label}>
            <Text size="xs" color="muted">
              {label}
            </Text>
            <Text size="sm" className="tabular-nums">
              {ms(value)}
            </Text>
          </div>
        ))}
      </Flex>
    </section>
  );
}

export function SandboxSeedOutputsSection({ seedOutputs }: { seedOutputs: SandboxV2Detail['seedOutputs'] }) {
  const steps = Object.entries(seedOutputs).filter(([, outputs]) => Object.keys(outputs).length > 0);
  if (steps.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" aria-labelledby="sandbox-v2-seed-outputs">
      <Heading level={3} id="sandbox-v2-seed-outputs">
        Seed outputs
      </Heading>
      <div className="divide-y divide-border">
        {steps.map(([step, outputs]) => (
          <div key={step} className="py-1.5">
            <Text weight="medium" size="sm">
              {step}
            </Text>
            <dl className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
              {Object.entries(outputs).map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="font-mono text-xs text-muted-foreground">{key}</dt>
                  <dd className="min-w-0 break-all font-mono text-xs">
                    {typeof value === 'string' ? value : JSON.stringify(value)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </section>
  );
}
